import React, { useEffect, useMemo, useState } from 'react';
import { applyTypography, BaseButton, useT } from '@repo/common';

const isKrw = (currency) => String(currency || '').toUpperCase() === 'KRW';

const formatMoney = (value, currency) =>
  isKrw(currency)
    ? `${Math.round(Number(value || 0)).toLocaleString('en-US')} KRW`
    : `${Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} $`;

/**
 * 환불 승인 확인 + 금액 입력.
 * 워크숍 정책에 50% 구간(예: 3~6일 전 취소)이 있어 전액이 아닌 금액을 넣을 수 있어야 한다.
 * 기본값은 전액. 카드는 입력 금액만큼 PG 취소, 계좌이체는 주최측 송금을 확인한 금액을 기록한다.
 */
const ApproveRefundModal = ({
  isOpen,
  onClose,
  onConfirm,
  loading = false,
  total = 0,
  currency = '$',
  isBankTransfer = false,
  userName = '',
  requestReason = '',
}) => {
  const t = useT();
  const totalNumber = Number(total || 0);
  const [amountText, setAmountText] = useState('');

  useEffect(() => {
    if (isOpen) {
      setAmountText(
        isKrw(currency) ? String(Math.round(totalNumber)) : totalNumber.toFixed(2),
      );
    }
  }, [isOpen, totalNumber, currency]);

  const amount = useMemo(() => {
    const parsed = Number(String(amountText).replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : NaN;
  }, [amountText]);

  const invalid =
    !Number.isFinite(amount) || amount <= 0 || amount > totalNumber + 1e-9;
  const isFull = !invalid && Math.abs(amount - totalNumber) < 1e-9;

  if (!isOpen) return null;

  const setPercent = (ratio) => {
    const value = totalNumber * ratio;
    setAmountText(isKrw(currency) ? String(Math.round(value)) : value.toFixed(2));
  };

  return (
    <>
      <div
        className='fixed inset-0 bg-surface-black40 z-[10000000000]'
        onClick={() => !loading && onClose?.()}
      />
      <div className='fixed inset-0 z-[10000000001] flex items-center justify-center px-[16px] py-[24px]'>
        <div
          role='dialog'
          aria-modal='true'
          className='bg-grayscale-gray00 w-full max-w-[520px] max-h-full overflow-y-auto rounded-[20px] p-[24px] tabletH:p-[32px] flex flex-col gap-[16px] shadow-2'
          onClick={(e) => e.stopPropagation()}
        >
          <div className='flex flex-col gap-[8px]'>
            <p className={`${applyTypography('title3')} text-text-primary`}>
              {isBankTransfer
                ? t('Mark refund as completed?', '환불 완료로 기록할까요?')
                : t('Approve this refund?', '이 환불을 승인할까요?')}
            </p>
            <p
              className={`${applyTypography('b14')} text-text-secondary`}
              style={{ lineHeight: 1.6 }}
            >
              {isBankTransfer
                ? t(
                    `Confirm only after the organizer has sent the refund to ${userName}. No PG call is made — the amount below is recorded as refunded and the guest gets a confirmation email.`,
                    `주최측이 ${userName} 님에게 환불을 송금한 것을 확인한 뒤 눌러 주세요. PG 호출은 없고 아래 금액이 환불 완료로 기록되며 게스트에게 완료 메일이 갑니다.`,
                  )
                : t(
                    'The amount below is refunded to the original payment method through the PG and the guest gets a confirmation email.',
                    '아래 금액이 PG를 통해 원결제 수단으로 환불되고 게스트에게 완료 메일이 갑니다.',
                  )}
            </p>
            {requestReason && (
              <p className={`${applyTypography('b13')} text-text-placeholder`}>
                {t('Guest’s reason:', '요청 사유:')} {requestReason}
              </p>
            )}
          </div>

          <label className='flex flex-col gap-[6px]'>
            <span className={`${applyTypography('b13Strong')} text-text-secondary`}>
              {t('Refund amount', '환불 금액')}
              <span className='text-text-placeholder'>
                {' '}
                · {t('Paid', '결제')} {formatMoney(totalNumber, currency)}
              </span>
            </span>
            <div className='flex items-center gap-[8px]'>
              <input
                type='number'
                inputMode='decimal'
                min='0'
                max={totalNumber}
                step={isKrw(currency) ? '1' : '0.01'}
                value={amountText}
                disabled={loading}
                onChange={(e) => setAmountText(e.target.value)}
                className={`flex-1 rounded-[8px] border bg-grayscale-gray00 px-[12px] py-[10px] typo-b16Strong text-text-primary focus:outline-none ${
                  invalid
                    ? 'border-functional-failed03'
                    : 'border-grayscale-gray02 focus:border-grayscale-gray04'
                }`}
              />
              <span className='typo-b14Strong text-text-secondary'>
                {isKrw(currency) ? 'KRW' : '$'}
              </span>
            </div>
            <div className='flex items-center gap-[8px]'>
              <button
                type='button'
                className={`typo-b12Strong underline ${isFull ? 'text-text-accent' : 'text-text-secondary'}`}
                onClick={() => setPercent(1)}
                disabled={loading}
              >
                {t('Full refund', '전액')}
              </button>
              <button
                type='button'
                className='typo-b12Strong underline text-text-secondary'
                onClick={() => setPercent(0.5)}
                disabled={loading}
              >
                {t('50% (policy tier)', '50% (정책 구간)')}
              </button>
            </div>
            {invalid && (
              <span className='typo-b12 text-functional-failed03'>
                {t(
                  'Enter an amount greater than 0 and no more than the paid amount.',
                  '0보다 크고 결제 금액 이하의 금액을 입력해 주세요.',
                )}
              </span>
            )}
            {!invalid && !isFull && (
              <span className='typo-b12 text-text-placeholder'>
                {t(
                  'Partial refund. The booking stays refunded; the difference is kept as the cancellation fee.',
                  '부분 환불입니다. 예약은 환불 처리되고 차액은 취소 수수료로 남습니다.',
                )}
              </span>
            )}
          </label>

          <div className='flex gap-[8px] justify-end'>
            <BaseButton
              type='text_basic'
              size='m'
              hideIcon
              onClick={onClose}
              isDisabled={loading}
            >
              {t('Not yet', '아직')}
            </BaseButton>
            <BaseButton
              type='primary'
              size='m'
              hideIcon
              onClick={() => !invalid && onConfirm?.(amount, isFull)}
              isLoading={loading}
              isDisabled={loading || invalid}
            >
              {isBankTransfer
                ? t('Mark refunded', '환불 완료')
                : t('Refund', '환불')}
              {!invalid ? ` · ${formatMoney(amount, currency)}` : ''}
            </BaseButton>
          </div>
        </div>
      </div>
    </>
  );
};

export default ApproveRefundModal;
